import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Activity,
  RefreshCw,
  Clock,
  Eye,
  LogOut,
  Building2,
  User,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { formatRelativeTime, formatDateTimeWithTZ } from '../../utils/timezone';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const actionConfig = {
  agency_context_start: { label: 'Entered Agency', icon: Building2, color: 'bg-amber-100 text-amber-600' },
  client_support_start: { label: 'Client Support', icon: Eye, color: 'bg-emerald-100 text-emerald-600' },
  support_context_end: { label: 'Exited Support', icon: LogOut, color: 'bg-slate-100 text-slate-600' },
};

/**
 * Support Activity Page - Phase 8
 * 
 * Audit log of all platform owner support actions.
 */
const SupportActivityPage = () => {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/owner-console/audit/history?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/owner-console/audit/active-sessions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setHistory(historyRes.data.items || []);
      setActiveSessions(sessionsRes.data.items || []);
    } catch (err) {
      console.error('Failed to fetch audit data:', err);
      setError(err.response?.data?.detail || 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return formatRelativeTime(dateStr);
  };
  
  const formatTimeWithTZ = (dateStr) => {
    if (!dateStr) return '';
    return formatDateTimeWithTZ(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Support Activity</h1>
          <p className="text-slate-500 mt-1">Audit log of platform owner support actions</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-amber-800 mb-4">Active Support Sessions</h2>
          <div className="space-y-3">
            {activeSessions.map((session) => (
              <div
                key={session.context_id}
                className="bg-white rounded-lg border border-amber-300 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Eye className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">
                      {session.actor_name || 'Platform Owner'} → {session.agency_name || session.effective_agency_id}
                    </p>
                    <p className="text-sm text-slate-500">
                      {session.support_mode} • Started {formatTimeWithTZ(session.started_at)}
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm font-medium">
                  Active
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit History */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-stone-900">Audit History</h2>
        </div>
        {history.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {history.map((entry, index) => {
              const config = actionConfig[entry.action] || {
                label: entry.action,
                icon: Activity,
                color: 'bg-slate-100 text-slate-600'
              };
              const Icon = config.icon;
              return (
                <div
                  key={entry.audit_id || index}
                  className="px-6 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`audit-entry-${entry.audit_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-medium text-stone-900">{config.label}</p>
                          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                            {entry.target_agency_id && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {entry.target_agency_id}
                              </span>
                            )}
                            {entry.target_user_id && (
                              <span className="flex items-center gap-1">
                                <User size={14} />
                                {entry.target_user_id}
                              </span>
                            )}
                          </div>
                          {entry.details?.reason && (
                            <p className="text-sm text-slate-500 mt-1">
                              Reason: {entry.details.reason}
                            </p>
                          )}
                          {entry.details?.support_mode && (
                            <p className="text-xs text-slate-400 mt-1">
                              Mode: {entry.details.support_mode}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 flex items-center gap-1 flex-shrink-0">
                          <Clock size={12} />
                          {formatTime(entry.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Activity size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No Activity Yet</h3>
            <p className="text-slate-500">Support actions will appear here as they occur.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportActivityPage;
